import React, { useEffect, useRef, useState } from "react";
import MaterialIcon from "../components/BuilderComponents/MaterialIcon";
import useLocalStorage from "../hooks/useLocalStorage";
import storageService from "../services/storageService";

const ResizableComponent = ({
  children,
  defaultSize = 200,
  resizeSide = "right",
  storageKey = null,
  range = [100, 500],
  className = "",
  collapsible = false
}) => {
  const [size, setSize] = useState(defaultSize);
  const [collapsed, setCollapsed] = useLocalStorage("is_left_sidebar_open", false);
  const [prevSize, setPrevSize] = useState(defaultSize);
  const [isHovered, setIsHovered] = useState(true);
  const [disableHover, setDisableHover] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const resizableRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (storageKey) {
      const storedSize = storageService.get(storageKey);
      const parsed = parseInt(storedSize, 10);
      if (collapsed) {
        setSize(18);
      } else if (storedSize && !isNaN(parsed)) {
        setSize(parsed);
      }
      setPrevSize(parsed);
    }
    setIsReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      let newSize;
      if (resizeSide === "right") {
        newSize = event.clientX - resizableRef.current.getBoundingClientRect().left;
      } else if (resizeSide === "left") {
        newSize = resizableRef.current.getBoundingClientRect().right - event.clientX;
      } else if (resizeSide === "bottom") {
        newSize = event.clientY - resizableRef.current.getBoundingClientRect().top;
      } else if (resizeSide === "top") {
        newSize = resizableRef.current.getBoundingClientRect().bottom - event.clientY;
      }

      const [minSize, maxSize] = range;
      if (newSize >= minSize && newSize <= maxSize) {
        setSize(newSize);
        setPrevSize(newSize);
        if (storageKey) {
          storageService.set(storageKey, newSize);
        }
      }
    };

    const handleMouseDown = () => {
      setIsResizing(true);

      document.body.style.cursor = resizeSide === "right" || resizeSide === "left" ? "col-resize" : "row-resize";

      document.body.style.userSelect = "none";

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseUp = () => {
      setIsResizing(false);

      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    const resizer = resizableRef.current?.querySelector(".resizer");
    if (resizer) {
      resizer.addEventListener("mousedown", handleMouseDown);
    }

    return () => {
      if (resizer) {
        resizer.removeEventListener("mousedown", handleMouseDown);
      }
    };
  }, [resizeSide, range, storageKey, isReady]);

  const toggleCollapse = () => {
    setDisableHover(true);
    if (collapsed) {
      setSize(prevSize);
    } else {
      setPrevSize(size);
      setSize(18);
    }
    setCollapsed(!collapsed);
    setTimeout(() => setDisableHover(false), 300);
  };

  const shouldExpand = collapsed && isHovered && !disableHover;
  const dynamicSize = shouldExpand ? prevSize : size;
  const styles = isReady
    ? {
        [resizeSide === "right" || resizeSide === "left" ? "width" : "height"]: dynamicSize,
        [resizeSide === "right" || resizeSide === "left" ? "height" : "width"]: "100%",
        transition: collapsed ? "width 0.3s ease" : undefined,
        position: "relative"
      }
    : {};

  return (
    <div className="flex_column">
      <div
        ref={resizableRef}
        className={`resizable_component ${className}`}
        style={styles}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setDisableHover(false);
        }}
      >
        {collapsible && (
          <button
            onClick={toggleCollapse}
            className="position_absolute d_flex align_center cursor_pointer border_full bg_light"
            style={{
              top: "58px",
              right: resizeSide === "right" ? "-13px" : "auto",
              left: resizeSide === "left" ? "4px" : "auto",
              zIndex: 4,
              borderRadius: "100%"
            }}
          >
            <MaterialIcon name={collapsed ? "chevron_right" : "chevron_left"} />
          </button>
        )}

        {isReady && (
          <>
            <div
              style={{
                opacity: !collapsed || shouldExpand ? 1 : 0,
                pointerEvents: !collapsed || shouldExpand ? "auto" : "none",
                transition: "opacity 0.3s"
              }}
            >
              {children}
            </div>
            <div className={`resizer resizer_${resizeSide} ${isResizing ? "resizing" : ""}`} />
          </>
        )}
      </div>
    </div>
  );
};

export default ResizableComponent;
